import React from 'react';
import { motion } from 'framer-motion';
import { IconType } from 'react-icons';
import {
  FaProjectDiagram,
  FaChartLine,
  FaHandshake,
  FaPython,
  FaDatabase,
  FaRobot,
} from 'react-icons/fa';
import {
  SiN8N,
  SiOpenai,
  SiLangchain,
  SiMilvus,
  SiAnthropic,
} from 'react-icons/si';
import { MdApi } from 'react-icons/md';
import './Hero.css';

const Hero: React.FC = () => {
  const stats = [
    { icon: FaHandshake, value: '30+', label: 'Enterprise Clients' },
    { icon: FaProjectDiagram, value: '50+', label: 'Systems Deployed' },
    { icon: FaChartLine, value: '95%+', label: 'Process Efficiency' },
    { icon: FaRobot, value: '24/7', label: 'Autonomous Uptime' },
  ];

  const skills: { icon: IconType; name: string }[] = [
    { icon: SiOpenai, name: 'OpenAI' },
    { icon: SiAnthropic, name: 'Anthropic' },
    { icon: SiLangchain, name: 'LangChain' },
    { icon: SiMilvus, name: 'Vector DB' },
    { icon: FaPython, name: 'Python' },
    { icon: SiN8N, name: 'n8n' },
    { icon: FaDatabase, name: 'Data Pipelines' },
    { icon: MdApi, name: 'API Integration' },
  ];

  return (
    <div className="hero section">
      <div className="bg-animation"></div>
      <div className="grid-overlay"></div>
      <div className="container">
        <div className="hero-content">
          <motion.div
            className="hero-text"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <motion.div
              className="hero-eyebrow"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <span className="status-dot"></span>
              AI Automation Engineer · Available for Engagements
            </motion.div>

            <motion.h1
              className="hero-title"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              Engineering <span className="gradient-text">Autonomous Systems</span> for the Enterprise
            </motion.h1>

            <motion.h2
              className="hero-subtitle"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              AI Agents · Workflow Automation · Intelligent Infrastructure
            </motion.h2>

            <motion.p
              className="hero-description"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
            >
              I design and deploy production-grade AI agents and automation infrastructure that
              reduce operational overhead, accelerate decision-making, and unlock measurable
              ROI. From LLM-powered orchestration to no-code workflow engineering, I help
              organizations transition from manual processes to scalable, autonomous systems.
            </motion.p>

            <motion.div
              className="hero-skills"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.8 }}
            >
              {skills.map((skill, index) => {
                const IconComponent = skill.icon;
                return (
                  <motion.div
                    key={skill.name}
                    className="skill-badge"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5, delay: 1 + index * 0.08 }}
                    whileHover={{ scale: 1.05 }}
                  >
                    {React.createElement(IconComponent as any)}
                    <span>{skill.name}</span>
                  </motion.div>
                );
              })}
            </motion.div>

            <motion.div
              className="hero-cta"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.2 }}
            >
              <a href="#projects" className="btn btn-primary">
                Review Engagements
              </a>
              <a
                href="https://calendly.com/haider-ai-engineer/new-meeting"
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-secondary"
              >
                Initiate Engagement
              </a>
            </motion.div>
          </motion.div>

          <motion.div
            className="hero-visual"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.5 }}
          >
            <div className="automation-graphic">
              <div className="central-node floating">
                <img
                  src="/profile.jpeg"
                  alt="Haider - AI Automation Engineer"
                  className="profile-img"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                  }}
                />
              </div>
              <div className="orbit-ring"></div>
              <div className="orbit-ring orbit-ring-2"></div>
              <div className="orbit-nodes">
                <div className="orbit-node node-1">
                  {React.createElement(SiOpenai as any)}
                </div>
                <div className="orbit-node node-2">
                  {React.createElement(FaDatabase as any)}
                </div>
                <div className="orbit-node node-3">
                  {React.createElement(FaPython as any)}
                </div>
                <div className="orbit-node node-4">
                  {React.createElement(SiLangchain as any)}
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        <motion.div
          className="hero-stats"
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.4 }}
        >
          {stats.map((stat, index) => {
            const IconComponent = stat.icon;
            return (
              <motion.div
                key={stat.label}
                className="stat-card glass-effect"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 1.6 + index * 0.1 }}
                whileHover={{ scale: 1.03 }}
              >
                <div className="stat-icon">{React.createElement(IconComponent as any)}</div>
                <div className="stat-value gradient-text">{stat.value}</div>
                <div className="stat-label">{stat.label}</div>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </div>
  );
};

export default Hero;